/**
 * API configuration utilities for frontend
 * Provides base URL for API calls according to CLAUDE.md requirements
 * Story 1.8: Enhanced with profile-aware API calls
 */

import { authenticatedFetch } from './auth'
import Cookies from 'js-cookie'

/**
 * Get the API base URL from environment variables
 * Returns the unified API Gateway URL for all services
 */
export async function getApiBaseUrl(): Promise<string> {
  // CLAUDE.md compliant: No fallbacks - fail fast if parameter missing
  const apiUrl = process.env.NEXT_PUBLIC_API_URL

  if (!apiUrl) {
    throw new Error('CRITICAL: NEXT_PUBLIC_API_URL environment variable is required but not set')
  }

  return apiUrl
}

/**
 * Get the active profile ID from cookies or default to 1
 * Story 1.8: Used to include profile_id in API calls
 */
export function getActiveProfileId(): number {
  if (typeof window === 'undefined') {
    // SSR: Try to get from cookie, default to 1
    return 1
  }

  const profileIdStr = Cookies.get('profile_id') || localStorage.getItem('activeProfileId') || '1'
  return parseInt(profileIdStr, 10) || 1
}

/**
 * Add profile_id query parameter to URL
 * Story 1.8: Helper to append profile_id to URLs
 */
export function addProfileIdToUrl(url: string, profileId?: number): string {
  const id = profileId ?? getActiveProfileId()
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}profile_id=${id}`
}

/**
 * Simple API client for making requests without /api/ prefixes
 * All paths route directly through the unified API Gateway
 * Story 1.8: Now includes profile_id in all requests automatically
 */
export class ApiClient {
  private baseUrl: string | null = null

  async getBaseUrl(): Promise<string> {
    if (!this.baseUrl) {
      this.baseUrl = await getApiBaseUrl()
    }
    return this.baseUrl
  }

  /**
   * Story 1.8: GET request with automatic profile_id injection
   */
  async get(service: string, path: string = '', options?: { profileId?: number; skipProfileId?: boolean }): Promise<Response> {
    const baseUrl = await this.getBaseUrl()
    let url = `${baseUrl}/${service}${path}`

    // Add profile_id unless explicitly skipped
    if (!options?.skipProfileId) {
      url = addProfileIdToUrl(url, options?.profileId)
    }

    return authenticatedFetch(url)
  }

  /**
   * Story 1.8: POST request with automatic profile_id injection in body
   */
  async post(service: string, path: string = '', body?: any, options?: { profileId?: number; skipProfileId?: boolean }): Promise<Response> {
    const baseUrl = await this.getBaseUrl()
    const url = `${baseUrl}/${service}${path}`

    // Add profile_id to body unless explicitly skipped
    const profileId = options?.profileId ?? getActiveProfileId()
    const enhancedBody = (!options?.skipProfileId && body && typeof body === 'object' && !Array.isArray(body))
      ? { ...body, profile_id: profileId }
      : body

    return authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: enhancedBody ? JSON.stringify(enhancedBody) : undefined,
    })
  }

  /**
   * Story 1.8: DELETE request with automatic profile_id injection
   */
  async delete(service: string, path: string = '', options?: { profileId?: number; skipProfileId?: boolean }): Promise<Response> {
    const baseUrl = await this.getBaseUrl()
    let url = `${baseUrl}/${service}${path}`

    // Add profile_id unless explicitly skipped
    if (!options?.skipProfileId) {
      url = addProfileIdToUrl(url, options?.profileId)
    }

    return authenticatedFetch(url, {
      method: 'DELETE',
    })
  }

  /**
   * Story 1.8: PUT request with automatic profile_id injection
   */
  async put(service: string, path: string = '', body?: any, options?: { profileId?: number; skipProfileId?: boolean }): Promise<Response> {
    const baseUrl = await this.getBaseUrl()
    const url = `${baseUrl}/${service}${path}`

    // Add profile_id to body unless explicitly skipped
    const profileId = options?.profileId ?? getActiveProfileId()
    const enhancedBody = (!options?.skipProfileId && body && typeof body === 'object' && !Array.isArray(body))
      ? { ...body, profile_id: profileId }
      : body

    return authenticatedFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: enhancedBody ? JSON.stringify(enhancedBody) : undefined,
    })
  }
}

export const apiClient = new ApiClient()